import React, { useCallback, useState } from "react";
import {
  Button,
  Grid,
  Header,
  Icon,
  Message,
  Segment,
  Divider,
} from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import { useDispatch } from "react-redux";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import ModalWrapper from "../../app/common/modals/modalWrapper";
import { closeModal } from "../../app/common/modals/modalSlice";
import { uploadBlobToStorage } from "../../app/firestore/firebaseService";
import MyTextInput from "../../app/common/form/MyTextInput";
import MyTextArea from "../../app/common/form/MyTextArea";
import MyCheckbox from "../../app/common/form/MyCheckbox";
import MySelectInput from "../../app/common/form/MySelectInput";
import {
  getFirestore,
  collection,
  addDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { app } from "../../app/config/firebase";
import { propertyTypeOptions } from "../../app/common/categoryData/categoryOptions";

// Role options for form sharing settings
const formRoleOptions = [
  { key: "buyer", text: "Buyer", value: "Buyer" },
  { key: "buyer2", text: "Buyer 2", value: "Buyer 2" },
  { key: "buyer3", text: "Buyer 3", value: "Buyer 3" },
  { key: "seller", text: "Seller", value: "Seller" },
  { key: "seller2", text: "Seller 2", value: "Seller 2" },
  { key: "seller3", text: "Seller 3", value: "Seller 3" },
  { key: "listingAgent", text: "Listing Agent", value: "Listing Agent" },
  { key: "buyerAgent", text: "Buyer Agent", value: "Buyer Agent" },
  { key: "title", text: "Title", value: "Title" },
  { key: "lender", text: "Lender", value: "Lender" },
];

// Role options for Send for Signatures (Buyer 2/3 represented as "Buyer", Seller 2/3 represented as "Seller")
const sendForSigsRoleOptions = [
  { key: "buyer", text: "Buyer (includes Buyer 2 & 3)", value: "Buyer" },
  { key: "seller", text: "Seller (includes Seller 2 & 3)", value: "Seller" },
  { key: "listingAgent", text: "Listing Agent", value: "Listing Agent" },
  { key: "buyerAgent", text: "Buyer Agent", value: "Buyer Agent" },
  { key: "title", text: "Title", value: "Title" },
  { key: "lender", text: "Lender", value: "Lender" },
];

// General document type classification, used to identify a form's purpose
// (e.g. so the signing-complete flow can recognize agency/listing agreements
// and suggest setting transaction.listingContractDateTime from the signed form).
const documentTypeOptions = [
  { key: "agency", text: "Agency", value: "Agency" },
  { key: "contract", text: "Contract", value: "Contract" },
  { key: "disclosure", text: "Disclosure", value: "Disclosure" },
  { key: "addendum", text: "Addendum", value: "Addendum" },
  { key: "notice", text: "Notice", value: "Notice" },
  { key: "receipt", text: "Receipt", value: "Receipt" },
  { key: "closing", text: "Closing", value: "Closing" },
  { key: "termination", text: "Termination", value: "Termination" },
  { key: "other", text: "Other", value: "Other" },
];

// Transaction status options for suggested forms
const formTransactionStatusOptions = [
  { key: "activeListing", text: "Active Listing", value: "Active Listing" },
  { key: "activeBuyer", text: "Active Buyer", value: "Active Buyer" },
  { key: "underContract", text: "Under Contract", value: "Under Contract" },
  { key: "complete", text: "Complete", value: "Complete" },
  { key: "archived", text: "Archived", value: "Archived" },
];

const dropzoneStyles = {
  textAlign: "center",
  cursor: "pointer",
  paddingTop: "36px",
  paddingBottom: "36px",
  border: "2px dashed #ccc",
  borderRadius: "8px",
  marginBottom: "20px",
};

const dropzoneActive = {
  border: "dashed 3px #2185d0",
  backgroundColor: "#f0f8ff",
};

export default function AdminCreateFormModal({
  selectedState,
  existingForm = null,
  isEditMode = false,
}) {
  const dispatch = useDispatch();
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [formMessageType, setFormMessageType] = useState("");

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const firstFile = acceptedFiles?.[0];
      if (!firstFile) return;

      if (firstFile.type !== "application/pdf") {
        setUploadError("Please upload a PDF file");
        return;
      }

      setUploadLoading(true);
      setUploadError(null);

      try {
        // Extract filename without extension for title/name
        const filenameWithoutExt = firstFile.name.replace(/\.pdf$/i, "");
        
        // Upload to storage/forms{State}/
        const storagePath = `forms${selectedState}/${firstFile.name}`;
        const blob = await fetch(URL.createObjectURL(firstFile)).then((r) =>
          r.blob()
        );
        await uploadBlobToStorage(storagePath, blob);

        setUploadedFile({
          name: firstFile.name,
          title: filenameWithoutExt,
          storagePath: storagePath,
        });
        setUploadSuccess(true);
        setUploadLoading(false);
      } catch (error) {
        console.error("Error uploading file:", error);
        setUploadError(error.message);
        setUploadLoading(false);
      }
    },
    [selectedState]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
  });

  // Get initial values - from existing form for edit mode, or from uploaded file for create mode
  const getInitialValues = () => {
    if (isEditMode && existingForm) {
      // Build sharing defaults from existing form
      const sharingDefaults = existingForm.emailSharingDefaults || {};
      // Build send for sigs defaults from existing form
      const sendForSigsDefaults = existingForm.emailDefaultsSendForSigs || {};
      return {
        title: existingForm.title || "",
        name: existingForm.name || "",
        brokerageReplacesParentFormTitle:
          existingForm.brokerageReplacesParentFormTitle || "",
        showBuyerAgent: existingForm.showBuyerAgent || false,
        showSellerAgent: existingForm.showSellerAgent || false,
        canBeTemplate: existingForm.canBeTemplate || false,
        isStateForm: existingForm.isStateForm || false,
        canApplyDeadlinesTemplate:
          existingForm.canApplyDeadlinesTemplate || false,
        canImportFromListing: existingForm.canImportFromListing || false,
        isFillableByClient: existingForm.isFillableByClient || false,
        canValidateForm: existingForm.canValidateForm || false,
        category: existingForm.category || "",
        documentType: existingForm.documentType || "",
        documentTypeSubtype: existingForm.documentTypeSubtype || "",
        canRemoveOtherSideSigsIfTransCancelled:
          existingForm.canRemoveOtherSideSigsIfTransCancelled || false,
        autoAddToNewTransaction: existingForm.autoAddToNewTransaction || false,
        isManagerOnly: existingForm.isManagerOnly || false,
        dontShowFormInAddDocumentModal:
          existingForm.dontShowFormInAddDocumentModal || false,
        hasFieldConversions: existingForm.hasFieldConversions || false,
        additionalFormsGroupId: existingForm.additionalFormsGroupId || "",
        canCustomizeEmailOnShare: existingForm.canCustomizeEmailOnShare || [],
        emailSharingDefaultsSubject: sharingDefaults.subjectPlusAddress || "",
        emailSharingDefaultsMessage: sharingDefaults.message || "",
        shareBuyerSubject: sharingDefaults.Buyer?.subjectPlusAddress || "",
        shareBuyerMessage: sharingDefaults.Buyer?.message || "",
        shareSellerSubject: sharingDefaults.Seller?.subjectPlusAddress || "",
        shareSellerMessage: sharingDefaults.Seller?.message || "",
        shareListingAgentSubject:
          sharingDefaults["Listing Agent"]?.subjectPlusAddress || "",
        shareListingAgentMessage:
          sharingDefaults["Listing Agent"]?.message || "",
        shareBuyerAgentSubject:
          sharingDefaults["Buyer Agent"]?.subjectPlusAddress || "",
        shareBuyerAgentMessage: sharingDefaults["Buyer Agent"]?.message || "",
        shareTitleSubject: sharingDefaults.Title?.subjectPlusAddress || "",
        shareTitleMessage: sharingDefaults.Title?.message || "",
        shareLenderSubject: sharingDefaults.Lender?.subjectPlusAddress || "",
        shareLenderMessage: sharingDefaults.Lender?.message || "",
        canCustomizeEmailOnSendForSigs:
          existingForm.canCustomizeEmailOnSendForSigs || [],
        sendForSigsBuyerSubject:
          sendForSigsDefaults.Buyer?.subjectPlusAddress || "",
        sendForSigsBuyerMessage: sendForSigsDefaults.Buyer?.message || "",
        sendForSigsSellerSubject:
          sendForSigsDefaults.Seller?.subjectPlusAddress || "",
        sendForSigsSellerMessage: sendForSigsDefaults.Seller?.message || "",
        sendForSigsListingAgentSubject:
          sendForSigsDefaults["Listing Agent"]?.subjectPlusAddress || "",
        sendForSigsListingAgentMessage:
          sendForSigsDefaults["Listing Agent"]?.message || "",
        sendForSigsBuyerAgentSubject:
          sendForSigsDefaults["Buyer Agent"]?.subjectPlusAddress || "",
        sendForSigsBuyerAgentMessage:
          sendForSigsDefaults["Buyer Agent"]?.message || "",
        sendForSigsTitleSubject:
          sendForSigsDefaults.Title?.subjectPlusAddress || "",
        sendForSigsTitleMessage: sendForSigsDefaults.Title?.message || "",
        sendForSigsLenderSubject:
          sendForSigsDefaults.Lender?.subjectPlusAddress || "",
        sendForSigsLenderMessage: sendForSigsDefaults.Lender?.message || "",
        isSuggestedTitleInAddDocModalSeller:
          existingForm.isSuggestedTitleInAddDocModal?.Seller || [],
        isSuggestedTitleInAddDocModalBuyer:
          existingForm.isSuggestedTitleInAddDocModal?.Buyer || [],
        doNotShareWithRole: existingForm.doNotShareWithRole || [],
        isBrokerageForm: existingForm.isBrokerageForm || false,
        brokerage: existingForm.brokerage || "",
        propertyType: existingForm.propertyType || "",
      };
    }
    return {
      title: uploadedFile?.title || "",
      name: uploadedFile?.title || "",
      brokerageReplacesParentFormTitle: "",
      showBuyerAgent: false,
      showSellerAgent: false,
      canBeTemplate: false,
      canApplyDeadlinesTemplate: false,
      canImportFromListing: false,
      canValidateForm: false,
      category: "",
      documentType: "",
      documentTypeSubtype: "",
      isStateForm: false,
      isFillableByClient: false,
      canRemoveOtherSideSigsIfTransCancelled: false,
      autoAddToNewTransaction: false,
      isManagerOnly: false,
      dontShowFormInAddDocumentModal: false,
      hasFieldConversions: false,
      additionalFormsGroupId: "",
      canCustomizeEmailOnShare: [],
      emailSharingDefaultsSubject: "",
      emailSharingDefaultsMessage: "",
      shareBuyerSubject: "",
      shareBuyerMessage: "",
      shareSellerSubject: "",
      shareSellerMessage: "",
      shareListingAgentSubject: "",
      shareListingAgentMessage: "",
      shareBuyerAgentSubject: "",
      shareBuyerAgentMessage: "",
      shareTitleSubject: "",
      shareTitleMessage: "",
      shareLenderSubject: "",
      shareLenderMessage: "",
      canCustomizeEmailOnSendForSigs: [],
      sendForSigsBuyerSubject: "",
      sendForSigsBuyerMessage: "",
      sendForSigsSellerSubject: "",
      sendForSigsSellerMessage: "",
      sendForSigsListingAgentSubject: "",
      sendForSigsListingAgentMessage: "",
      sendForSigsBuyerAgentSubject: "",
      sendForSigsBuyerAgentMessage: "",
      sendForSigsTitleSubject: "",
      sendForSigsTitleMessage: "",
      sendForSigsLenderSubject: "",
      sendForSigsLenderMessage: "",
      isSuggestedTitleInAddDocModalSeller: [],
      isSuggestedTitleInAddDocModalBuyer: [],
      doNotShareWithRole: [],
      isBrokerageForm: false,
      brokerage: "",
      propertyType: "",
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setFormMessage("");
    try {
      const db = getFirestore(app);
      const collectionName = `forms${selectedState}`;

      // Build emailSharingDefaults map from individual role fields
      const emailSharingDefaults = {
        subjectPlusAddress: values.emailSharingDefaultsSubject || "",
        message: values.emailSharingDefaultsMessage || "",
      };
      if (values.canCustomizeEmailOnShare?.includes("Buyer")) {
        emailSharingDefaults.Buyer = {
          subjectPlusAddress: values.shareBuyerSubject || "",
          message: values.shareBuyerMessage || "",
        };
      }
      if (values.canCustomizeEmailOnShare?.includes("Seller")) {
        emailSharingDefaults.Seller = {
          subjectPlusAddress: values.shareSellerSubject || "",
          message: values.shareSellerMessage || "",
        };
      }
      if (values.canCustomizeEmailOnShare?.includes("Listing Agent")) {
        emailSharingDefaults["Listing Agent"] = {
          subjectPlusAddress: values.shareListingAgentSubject || "",
          message: values.shareListingAgentMessage || "",
        };
      }
      if (values.canCustomizeEmailOnShare?.includes("Buyer Agent")) {
        emailSharingDefaults["Buyer Agent"] = {
          subjectPlusAddress: values.shareBuyerAgentSubject || "",
          message: values.shareBuyerAgentMessage || "",
        };
      }
      if (values.canCustomizeEmailOnShare?.includes("Title")) {
        emailSharingDefaults.Title = {
          subjectPlusAddress: values.shareTitleSubject || "",
          message: values.shareTitleMessage || "",
        };
      }
      if (values.canCustomizeEmailOnShare?.includes("Lender")) {
        emailSharingDefaults.Lender = {
          subjectPlusAddress: values.shareLenderSubject || "",
          message: values.shareLenderMessage || "",
        };
      }

      // Build emailDefaultsSendForSigs map from individual role fields
      const emailDefaultsSendForSigs = {};
      if (values.canCustomizeEmailOnSendForSigs?.includes("Buyer")) {
        emailDefaultsSendForSigs.Buyer = {
          subjectPlusAddress: values.sendForSigsBuyerSubject || "",
          message: values.sendForSigsBuyerMessage || "",
        };
      }
      if (values.canCustomizeEmailOnSendForSigs?.includes("Seller")) {
        emailDefaultsSendForSigs.Seller = {
          subjectPlusAddress: values.sendForSigsSellerSubject || "",
          message: values.sendForSigsSellerMessage || "",
        };
      }
      if (values.canCustomizeEmailOnSendForSigs?.includes("Listing Agent")) {
        emailDefaultsSendForSigs["Listing Agent"] = {
          subjectPlusAddress: values.sendForSigsListingAgentSubject || "",
          message: values.sendForSigsListingAgentMessage || "",
        };
      }
      if (values.canCustomizeEmailOnSendForSigs?.includes("Buyer Agent")) {
        emailDefaultsSendForSigs["Buyer Agent"] = {
          subjectPlusAddress: values.sendForSigsBuyerAgentSubject || "",
          message: values.sendForSigsBuyerAgentMessage || "",
        };
      }
      if (values.canCustomizeEmailOnSendForSigs?.includes("Title")) {
        emailDefaultsSendForSigs.Title = {
          subjectPlusAddress: values.sendForSigsTitleSubject || "",
          message: values.sendForSigsTitleMessage || "",
        };
      }
      if (values.canCustomizeEmailOnSendForSigs?.includes("Lender")) {
        emailDefaultsSendForSigs.Lender = {
          subjectPlusAddress: values.sendForSigsLenderSubject || "",
          message: values.sendForSigsLenderMessage || "",
        };
      }

      const formData = {
        title: values.title,
        name: values.name,
        brokerageReplacesParentFormTitle:
          values.brokerageReplacesParentFormTitle || "",
        showBuyerAgent: values.showBuyerAgent,
        showSellerAgent: values.showSellerAgent,
        canBeTemplate: values.canBeTemplate,
        canApplyDeadlinesTemplate: values.canApplyDeadlinesTemplate,
        canImportFromListing: values.canImportFromListing,
        canValidateForm: values.canValidateForm,
        category: values.category,
        documentType: values.documentType || "",
        documentTypeSubtype: values.documentTypeSubtype || "",
        isStateForm: values.isStateForm,
        isFillableByClient: values.isFillableByClient,
        canRemoveOtherSideSigsIfTransCancelled:
          values.canRemoveOtherSideSigsIfTransCancelled,
        autoAddToNewTransaction: values.autoAddToNewTransaction,
        isManagerOnly: values.isManagerOnly,
        dontShowFormInAddDocumentModal: values.dontShowFormInAddDocumentModal,
        hasFieldConversions: values.hasFieldConversions,
        additionalFormsGroupId: values.additionalFormsGroupId || "",
        canCustomizeEmailOnShare: values.canCustomizeEmailOnShare || [],
        emailSharingDefaults: emailSharingDefaults,
        canCustomizeEmailOnSendForSigs:
          values.canCustomizeEmailOnSendForSigs || [],
        emailDefaultsSendForSigs: emailDefaultsSendForSigs,
        isSuggestedTitleInAddDocModal: {
          Seller: values.isSuggestedTitleInAddDocModalSeller || [],
          Buyer: values.isSuggestedTitleInAddDocModalBuyer || [],
        },
        doNotShareWithRole: values.doNotShareWithRole || [],
        isBrokerageForm: values.isBrokerageForm || false,
        brokerage: values.brokerage || "",
        propertyType: values.propertyType || "",
      };

      if (isEditMode && existingForm?.id) {
        // Update existing form
        const docRef = doc(db, collectionName, existingForm.id);
        await updateDoc(docRef, formData);
        setFormMessage(`Form "${values.title}" updated successfully`);
      } else {
        // Create new form
        formData.createdAt = new Date();
        await addDoc(collection(db, collectionName), formData);
        setFormMessage(
          `Form "${values.title}" created successfully in ${collectionName}`
        );
      }
      setFormMessageType("success");

      setTimeout(() => {
        dispatch(closeModal({ modalType: "AdminCreateFormModal" }));
      }, 1500);
    } catch (error) {
      console.error("Error saving form:", error);
      setFormMessage(`Error saving form: ${error.message}`);
      setFormMessageType("error");
    } finally {
      setSubmitting(false);
    }
  };

  // For create mode, show upload UI first if no file uploaded yet
  if (!isEditMode && !uploadSuccess) {
    return (
      <ModalWrapper size="small" header={`Create New Form - ${selectedState}`}>
        <Segment>
          <div
            {...getRootProps()}
            style={
              isDragActive
                ? { ...dropzoneStyles, ...dropzoneActive }
                : dropzoneStyles
            }
          >
            <input {...getInputProps()} />
            <Icon name="file pdf outline" size="huge" color="grey" />
            <Header color="grey" style={{ marginTop: "6px" }}>
              Drag and drop a PDF form here
              <br />
              or click to select a file
            </Header>
            <p style={{ color: "#888" }}>
              The PDF will be uploaded to storage/forms{selectedState}/
            </p>
          </div>
          {uploadLoading && (
            <Message info>
              <Icon name="spinner" loading />
              Uploading...
            </Message>
          )}
          {uploadError && (
            <Message negative>
              <Message.Header>Upload Error</Message.Header>
              <p>{uploadError}</p>
            </Message>
          )}
          <Button
            onClick={() =>
              dispatch(closeModal({ modalType: "AdminCreateFormModal" }))
            }
          >
            Cancel
          </Button>
        </Segment>
      </ModalWrapper>
    );
  }

  // Show the form (either after upload for create, or immediately for edit)
  return (
    <ModalWrapper
      size="large"
      header={
        isEditMode
          ? `Edit Form - ${selectedState}`
          : `Create New Form - ${selectedState}`
      }
    >
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={Yup.object({
          title: Yup.string().required("Unique name is required"),
          name: Yup.string()
            .matches(
              /^[a-zA-Z0-9 _-]*$/,
              "Only letters, numbers, underscore, dash, and spaces are allowed."
            )
            .required("Display name is required"),
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, dirty, values }) => (
          <Form className="ui form">
            <Segment>
              {uploadedFile && (
                <Message positive>
                  <Message.Header>File Uploaded Successfully</Message.Header>
                  <p>Uploaded: {uploadedFile.name}</p>
                </Message>
              )}
              <Grid>
                <Grid.Row>
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextInput
                      name="title"
                      label="Unique Name of File"
                      placeholder="Include brokerage abbreviation (e.g., CB_ExclusiveRightToSell)"
                    />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextInput
                      name="name"
                      label="Display Name"
                      placeholder="Name shown in transaction (e.g., Exclusive Right to Sell)"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextInput name="category" label="Searchable field" />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column mobile={16} computer={8}>
                    <MySelectInput
                      name="documentType"
                      label="Document Type"
                      placeholder="e.g., Agency"
                      options={documentTypeOptions}
                      clearable
                    />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextInput
                      name="documentTypeSubtype"
                      label="Document Type Subtype"
                      placeholder="e.g., Listing Agreement, Buyer Agency Agreement"
                    />
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column mobile={16} computer={8}>
                    <MyCheckbox
                      name="showBuyerAgent"
                      label="Show Buyer Agent"
                    />
                    <MyCheckbox
                      name="showSellerAgent"
                      label="Show Seller Agent"
                    />
                    <MyCheckbox
                      name="canBeTemplate"
                      label="Can be a Form Template"
                    />
                    <MyCheckbox name="isStateForm" label="Is State Form" />
                    <MyCheckbox
                      name="isFillableByClient"
                      label="Can Client Fill Out Form"
                    />
                    <MyCheckbox
                      name="canRemoveOtherSideSigsIfTransCancelled"
                      label="Can Remove Other Side Sigs If Trans Cancelled"
                    />
                    <MyCheckbox
                      name="canValidateForm"
                      label="Can Validate Form"
                    />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={8}>
                    <MyCheckbox
                      name="autoAddToNewTransaction"
                      label="Automatically Add to New Transaction"
                    />
                    <MyCheckbox name="isManagerOnly" label="Manager Only" />
                    <MyCheckbox
                      name="dontShowFormInAddDocumentModal"
                      label="Don't Show in Add Document Modal (expired or not used)"
                    />
                    <MyCheckbox
                      name="hasFieldConversions"
                      label="Has Field Conversions"
                    />
                    <MyCheckbox
                      name="canApplyDeadlinesTemplate"
                      label="Can Apply Deadlines Template"
                    />
                    <MyCheckbox
                      name="canImportFromListing"
                      label="Can Import from Listing"
                    />
                    <MySelectInput
                      name="propertyType"
                      label="Property Type"
                      options={propertyTypeOptions}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Divider />
                <Grid.Row>
                  <Grid.Column mobile={16} computer={4}>
                    <MyCheckbox
                      name="isBrokerageForm"
                      label="Is Brokerage Form?"
                    />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={4}>
                    <MyTextInput name="brokerage" label="Brokerage" />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={5}>
                    <MyTextInput
                      name="brokerageReplacesParentFormTitle"
                      label="State Form to Override (optional)"
                      placeholder="Enter the name of the state form this replaces"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Divider />

                <Grid.Row>
                  <Grid.Column mobile={16} computer={4}>
                    <MyTextInput
                      name="additionalFormsGroupId"
                      label="Additional Forms Group ID"
                      placeholder="Group ID"
                    />
                  </Grid.Column>
                </Grid.Row>
                <Divider />
                <Grid.Row>
                  <Grid.Column mobile={16} computer={16}>
                    <Header size="small" color="blue">
                      Email Sharing Options
                    </Header>
                    <MySelectInput
                      name="canCustomizeEmailOnShare"
                      label="Can Customize Email on Share (select roles)"
                      options={formRoleOptions}
                      multiple={true}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextInput
                      name="emailSharingDefaultsSubject"
                      label="Default Email Subject (fallback)"
                      placeholder="Default subject line for sharing"
                    />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={8}>
                    <MyTextArea
                      name="emailSharingDefaultsMessage"
                      label="Default Email Message (fallback)"
                      placeholder="Default email body for sharing"
                      rows={3}
                    />
                  </Grid.Column>
                </Grid.Row>
                {values.canCustomizeEmailOnShare?.includes("Buyer") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="shareBuyerSubject"
                        label="Buyer - Subject"
                        placeholder="Subject for Buyer (includes Buyer 2 & 3)"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="shareBuyerMessage"
                        label="Buyer - Message"
                        placeholder="Message for Buyer"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnShare?.includes("Seller") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="shareSellerSubject"
                        label="Seller - Subject"
                        placeholder="Subject for Seller (includes Seller 2 & 3)"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="shareSellerMessage"
                        label="Seller - Message"
                        placeholder="Message for Seller"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnShare?.includes("Listing Agent") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="shareListingAgentSubject"
                        label="Listing Agent - Subject"
                        placeholder="Subject for Listing Agent"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="shareListingAgentMessage"
                        label="Listing Agent - Message"
                        placeholder="Message for Listing Agent"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnShare?.includes("Buyer Agent") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="shareBuyerAgentSubject"
                        label="Buyer Agent - Subject"
                        placeholder="Subject for Buyer Agent"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="shareBuyerAgentMessage"
                        label="Buyer Agent - Message"
                        placeholder="Message for Buyer Agent"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnShare?.includes("Title") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="shareTitleSubject"
                        label="Title - Subject"
                        placeholder="Subject for Title"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="shareTitleMessage"
                        label="Title - Message"
                        placeholder="Message for Title"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnShare?.includes("Lender") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="shareLenderSubject"
                        label="Lender - Subject"
                        placeholder="Subject for Lender"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="shareLenderMessage"
                        label="Lender - Message"
                        placeholder="Message for Lender"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                <Divider />
                <Grid.Row>
                  <Grid.Column mobile={16} computer={16}>
                    <Header size="small" color="blue">
                      Customize Email when Sending for Signatures
                    </Header>
                    <MySelectInput
                      name="canCustomizeEmailOnSendForSigs"
                      label="Select roles that can customize email when sending for signatures"
                      options={sendForSigsRoleOptions}
                      multiple={true}
                    />
                  </Grid.Column>
                </Grid.Row>
                {values.canCustomizeEmailOnSendForSigs?.includes("Buyer") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="sendForSigsBuyerSubject"
                        label="Buyer - Default Subject"
                        placeholder="Subject for Buyer (includes Buyer 2 & 3)"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="sendForSigsBuyerMessage"
                        label="Buyer - Default Message"
                        placeholder="Message for Buyer"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnSendForSigs?.includes("Seller") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="sendForSigsSellerSubject"
                        label="Seller - Default Subject"
                        placeholder="Subject for Seller (includes Seller 2 & 3)"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="sendForSigsSellerMessage"
                        label="Seller - Default Message"
                        placeholder="Message for Seller"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnSendForSigs?.includes(
                  "Listing Agent"
                ) && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="sendForSigsListingAgentSubject"
                        label="Listing Agent - Default Subject"
                        placeholder="Subject for Listing Agent"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="sendForSigsListingAgentMessage"
                        label="Listing Agent - Default Message"
                        placeholder="Message for Listing Agent"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnSendForSigs?.includes(
                  "Buyer Agent"
                ) && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="sendForSigsBuyerAgentSubject"
                        label="Buyer Agent - Default Subject"
                        placeholder="Subject for Buyer Agent"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="sendForSigsBuyerAgentMessage"
                        label="Buyer Agent - Default Message"
                        placeholder="Message for Buyer Agent"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnSendForSigs?.includes("Title") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="sendForSigsTitleSubject"
                        label="Title - Default Subject"
                        placeholder="Subject for Title"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="sendForSigsTitleMessage"
                        label="Title - Default Message"
                        placeholder="Message for Title"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                {values.canCustomizeEmailOnSendForSigs?.includes("Lender") && (
                  <Grid.Row>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextInput
                        name="sendForSigsLenderSubject"
                        label="Lender - Default Subject"
                        placeholder="Subject for Lender"
                      />
                    </Grid.Column>
                    <Grid.Column mobile={16} computer={8}>
                      <MyTextArea
                        name="sendForSigsLenderMessage"
                        label="Lender - Default Message"
                        placeholder="Message for Lender"
                        rows={2}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}
                <Divider />
                <Grid.Row>
                  <Grid.Column mobile={16} computer={16}>
                    <Header size="small" color="blue">
                      Suggested Forms Options
                    </Header>
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={8}>
                    <MySelectInput
                      name="isSuggestedTitleInAddDocModalSeller"
                      label="Suggest for Seller Transactions"
                      options={formTransactionStatusOptions}
                      multiple={true}
                    />
                  </Grid.Column>
                  <Grid.Column mobile={16} computer={8}>
                    <MySelectInput
                      name="isSuggestedTitleInAddDocModalBuyer"
                      label="Suggest for Buyer Transactions"
                      options={formTransactionStatusOptions}
                      multiple={true}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Divider />
                <Grid.Row>
                  <Grid.Column mobile={16} computer={16}>
                    <Header size="small" color="blue">
                      Sharing Restrictions
                    </Header>
                    <MySelectInput
                      name="doNotShareWithRole"
                      label="Do NOT Share With These Roles"
                      options={formRoleOptions}
                      multiple={true}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column mobile={16} computer={16}>
                    <Button
                      loading={isSubmitting}
                      disabled={!dirty || isSubmitting}
                      type="submit"
                      size="large"
                      color="blue"
                      content={isEditMode ? "Update Form" : "Create Form"}
                    />
                    <Button
                      type="button"
                      size="large"
                      onClick={() =>
                        dispatch(
                          closeModal({ modalType: "AdminCreateFormModal" })
                        )
                      }
                    >
                      Cancel
                    </Button>
                    {formMessage && (
                      <Message
                        positive={formMessageType === "success"}
                        negative={formMessageType === "error"}
                        style={{ marginTop: "10px" }}
                      >
                        {formMessage}
                      </Message>
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>
          </Form>
        )}
      </Formik>
    </ModalWrapper>
  );
}
